/* Collection-scoped, demand-driven availability ordering. No catalog writes. */
(() => {
  'use strict';
  if (window.swStockOrder) return;
  const attr = 'data-limoniapps-discountninja-product-handle';
  const availableAttr = 'data-limoniapps-discountninja-product-available';
  const cache = new Map();
  let run = 0, controller;
  const state = window.swStockOrder = { version: 1, status: 'idle', metrics: {} };
  function keyFor(url) {
    const u = new URL(url); ['page', 'section_id', 'view', '_sw_stock'].forEach(k => u.searchParams.delete(k));
    u.searchParams.sort(); return u.href;
  }
  async function get(url, signal) {
    const response = await fetch(url, {signal: AbortSignal.any([signal,AbortSignal.timeout(15000)]), credentials: 'same-origin'});
    if (!response.ok) throw Error('HTTP ' + response.status);
    return response.text();
  }
  function remember(key, entry) {
    if (cache.size >= 5 && !cache.has(key)) cache.delete(cache.keys().next().value);
    cache.set(key, entry);
    try { sessionStorage.setItem('sw-stock-v1',JSON.stringify({key,entry})); } catch (_) { /* Optional cache. */ }
  }
  function cardsFrom(root, map) {
    root.querySelectorAll('.collection-products-grid > [' + attr + ']').forEach(n => map.set(n.getAttribute(attr), n.cloneNode(true)));
  }
  async function start() {
    const root = document.querySelector('[data-sw-stock]');
    if (!root) return;
    const grid = root.querySelector('.collection-products-grid');
    if (!grid) return;
    const token = ++run;
    controller?.abort(); controller = new AbortController();
    const signal = controller.signal;
    const startTime = performance.now();
    const url = new URL(location.href), key = keyFor(url), size = Number(root.dataset.swSize);
    // Boost can finish an older facet request after a newer one. Never accept its
    // stale filter chips/counts or initial cards: restore the URL's current state.
    const queryNode=root.querySelector('[data-sw-stock-query]');
    if(queryNode) {
      const source=JSON.parse(queryNode.textContent);
      const normalize=pairs=>JSON.stringify(pairs.filter(Boolean).map(([k,v])=>[k,k.startsWith('filter.v.price.')?String(Number(v)):String(v)]).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b))));
      const requested=[...url.searchParams].filter(([k,v])=>k.startsWith('filter.')&&v!=='');
      if(source.sort!==(url.searchParams.get('sort_by')||source.defaultSort)||normalize(source.filters)!==normalize(requested)) {
        // A fresh server response may legitimately ignore an unsupported filter.
        // Restore at most once per URL, then use Shopify's canonical result.
        let restored=true;
        try {restored=sessionStorage.getItem('sw-stock-restored')===url.href;if(!restored)sessionStorage.setItem('sw-stock-restored',url.href);}catch(_) {/* Storage can be disabled. */}
        if(!restored){root.dataset.swState='loading';state.status='restoring';location.replace(url.href);return;}
      } else {
        try {sessionStorage.removeItem('sw-stock-restored');}catch(_) {}
      }
    }
    const page = Math.max(1, Number(url.searchParams.get('page')) || 1);
    const end = page * size, begin = end - size;
    const initial = grid.innerHTML;
    const nativePagination = root.querySelector('.pagination');
    const originalPaging = nativePagination?.outerHTML || '';
    root.querySelectorAll('.sw-stock-status,.sw-stock-paging').forEach(n => n.remove());
    const status = document.createElement('p'); status.className = 'sw-stock-status';
    status.setAttribute('role', 'status'); status.textContent = 'טוענים את המוצרים לפי זמינות…';
    grid.before(status);
    root.dataset.swState = 'loading';
    // Only the first native page's available subsequence is a safe immediate prefix.
    root.dataset.swPage = String(page);
    state.status = 'loading';
    const cards = new Map(); cardsFrom(root, cards);
    let entry = cache.get(key);
    if(!entry) {try {const stored=JSON.parse(sessionStorage.getItem('sw-stock-v1'));if(stored?.key===key)entry=stored.entry;}catch(_) {/* Optional cache. */}}
    if (!entry || Date.now() - entry.time > 30000) entry = {time:Date.now(), records:[], next:1, pages:1, total:null};
    const assertCurrent = () => { if (token !== run || key !== keyFor(location.href) || !root.isConnected) throw new DOMException('Stale', 'AbortError'); };
    let metadataRequests = 0, cardRequests = 0;
    try {
      let available = entry.records.filter(r => r.available);
      // At most 100 metadata pages / Shopify's 25,000-record pagination limit.
      while (available.length < end && entry.next <= entry.pages) {
        if (entry.next > 100) throw Error('Pagination limit');
        const batchEnd = entry.total === null ? entry.next : Math.min(entry.next+2,entry.pages);
        const batch = await Promise.all(Array.from({length:batchEnd-entry.next+1},(_,i)=>entry.next+i).map(async n=>{
          const u = new URL(key);u.searchParams.set('view','sw-stock-data');u.searchParams.set('page',String(n));
          const data=JSON.parse(await get(u,signal));metadataRequests++;return data;
        }));assertCurrent();
        for(const data of batch) {
        if (data.version !== 1 || data.page !== entry.next || !Array.isArray(data.products) || data.total > 25000) throw Error('Invalid metadata');
        if (entry.total !== null && entry.total !== data.total) throw Error('Collection changed');
        const seen = new Set(entry.records.map(r => r.handle));
        for (const [i, pair] of data.products.entries()) {
          if (typeof pair[0] !== 'string' || typeof pair[1] !== 'boolean' || seen.has(pair[0])) throw Error('Invalid collection sequence');
          seen.add(pair[0]); entry.records.push({handle:pair[0], available:pair[1], sourcePage:Math.floor((data.offset + i)/size)+1});
        }
        entry.pages = data.pages; entry.total = data.total; entry.next++;
        }
        remember(key, entry);
        available = entry.records.filter(r => r.available);
      }
      const complete = entry.next > entry.pages;
      if (complete && entry.records.length !== entry.total) throw Error('Incomplete collection');
      const ordered = complete ? available.concat(entry.records.filter(r => !r.available)) : available;
      const selected = ordered.slice(begin,end);
      // Reuse original native cards; only fetch native pages containing missing visible cards.
      const missingPages = [...new Set(selected.filter(r => !cards.has(r.handle)).map(r => r.sourcePage))];
      for (let i=0;i<missingPages.length;i+=3) {
        await Promise.all(missingPages.slice(i,i+3).map(async nativePage => {
          const u = new URL(key); u.searchParams.set('page',String(nativePage)); u.searchParams.set('section_id',root.dataset.swSection);
          const html = await get(u,signal); cardRequests++; assertCurrent();
          cardsFrom(new DOMParser().parseFromString(html,'text/html'),cards);
        }));
      }
      assertCurrent();
      for (const record of selected) {
        const card = cards.get(record.handle);
        if (!card || (card.getAttribute(availableAttr)==='true') !== record.available) { cache.delete(key); throw Error('Availability changed'); }
      }
      const fragment = document.createDocumentFragment();
      const promo = [...grid.children].find(n => !n.hasAttribute(attr) && !n.classList.contains('sw-stock-heading') && n.querySelector('img'));
      selected.forEach((record,i) => {
        if(i===1 && promo) fragment.append(promo.cloneNode(true));
        if(!record.available && begin+i===available.length) {
          const divider=document.createElement('div'); divider.className='sw-stock-divider';
          const heading=document.createElement('h2'); heading.textContent='אזלו מהמלאי';divider.append(heading);fragment.append(divider);
        }
        fragment.append(cards.get(record.handle));
      });
      if (entry.total !== 0) grid.replaceChildren(fragment);
      nativePagination?.remove();
      const pages = Math.ceil(entry.total / size);
      if (pages > 1) {
        const nav = document.createElement('nav'); nav.className='pagination large-row sw-stock-paging'; nav.setAttribute('aria-label','עמודי מוצרים');
        const link=(n,text,current=false)=> { const span=document.createElement('span');span.className=current?'page current':'page'; if(current) {span.textContent=text;span.setAttribute('aria-current','page');}else{const a=document.createElement('a');const u=new URL(key);u.searchParams.set('page',String(n));a.href=u.href;a.textContent=text;span.append(a);}nav.append(span);};
        if(page>1) link(page-1,'« הקודם');
        const visible = [...new Set([1,page-1,page,page+1,pages])].filter(n=>n>=1&&n<=pages).sort((a,b)=>a-b);
        let previous=0;visible.forEach(n=>{if(previous&&n-previous>1)nav.append(document.createTextNode(' … '));link(n,String(n),n===page);previous=n;});
        if(page<pages)link(page+1,'הבא »');grid.after(nav);
      }
      root.dataset.swState='ready';status.remove();
      state.status='ready'; state.metrics={page,total:entry.total,scanned:entry.records.length,complete,availableScanned:available.length,metadataRequests,cardRequests,ms:Math.round(performance.now()-startTime)};
      window.updateCartLimitButtons?.();
      document.dispatchEvent(new CustomEvent('sw-stock-ready',{detail:state.metrics}));
    } catch(error) {
      if (error.name === 'AbortError' || token !== run) return;
      cache.delete(key);try{sessionStorage.removeItem('sw-stock-v1');}catch(_){}grid.innerHTML=initial;root.dataset.swState='fallback';
      root.querySelector('.sw-stock-paging')?.remove();
      if(!root.querySelector('.pagination')&&originalPaging)grid.insertAdjacentHTML('afterend',originalPaging);
      status.textContent='לא ניתן להשלים כרגע את המיון לפי זמינות. מוצג הסדר הרגיל.';
      const retry=document.createElement('button');retry.type='button';retry.className='btn';retry.textContent='ניסיון נוסף';retry.addEventListener('click',start);status.append(' ',retry);
      state.status='fallback';state.error=String(error);
    }
  }
  document.addEventListener('filters-applied',start);
  if(!history.state?.searchParams)history.replaceState({...history.state,searchParams:location.search.slice(1)},'',location.href);
  // Native Boost owns filter history. Pagination navigation is full-page and preserves
  // every parameter, so reload, direct links and browser back retain the chosen state.
  window.addEventListener('pageshow',e=>{if(e.persisted)start();});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();
